import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

const FLAG_META = {
  done_not_on_plan: { label: "Done — not on plan", cls: "border-[#C0392B]/40 bg-[#C0392B]/10 text-[#C0392B]" },
  planned_not_done: { label: "Planned — never done", cls: "border-[#B7791F]/40 bg-[#B7791F]/10 text-[#8a5d14]" },
  frequency_below_plan: { label: "Below planned frequency", cls: "border-[#B7791F]/40 bg-[#B7791F]/10 text-[#8a5d14]" },
  refusals: { label: "Refusals", cls: "border-[#C0392B]/40 bg-[#C0392B]/10 text-[#C0392B]" },
};
const SUSPECT_META = {
  care_gap: { label: "Care may have changed", cls: "border-[#C0392B]/40 bg-[#C0392B]/10 text-[#C0392B]" },
  plan_gap: { label: "Not on plan — office", cls: "border-[#C0392B]/40 bg-[#C0392B]/10 text-[#C0392B]" },
  entry_gap: { label: "Possibly not entering", cls: "border-[#B7791F]/40 bg-[#B7791F]/10 text-[#8a5d14]" },
  unplanned_single: { label: "One caregiver, not on plan", cls: "border-[#B7791F]/40 bg-[#B7791F]/10 text-[#8a5d14]" },
};

export function PlanVsActualCard({ clientId, clientName }) {
  const [weeks, setWeeks] = useState(3);
  const [data, setData] = useState(null);
  const [patterns, setPatterns] = useState(null);
  const [draft, setDraft] = useState(null);
  const [picked, setPicked] = useState({});
  const [nudges, setNudges] = useState([]);
  const [draftOpen, setDraftOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async (w = weeks) => {
    setBusy(true);
    try {
      const [pva, pat, nud] = await Promise.all([
        api.get(`/clients/${clientId}/plan-vs-actual`, { params: { weeks: w } }),
        api.get(`/clients/${clientId}/code-patterns`, { params: { weeks: w } }),
        api.get(`/clients/${clientId}/code-nudges`),
      ]);
      setData(pva.data);
      setPatterns(pat.data);
      setNudges(nud.data.nudges || []);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  useEffect(() => { load(weeks); }, [clientId, weeks]); // eslint-disable-line

  const openDraft = async () => {
    if (draftOpen) { setDraftOpen(false); return; }
    try {
      const { data } = await api.get(`/clients/${clientId}/draft-from-actual`, { params: { weeks } });
      setDraft(data);
      const pre = {};
      (data.proposals || []).forEach((p) => { if (!p.already_on_plan) pre[p.code] = true; });
      setPicked(pre);
      setDraftOpen(true);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const applyDraft = async () => {
    const codes = Object.keys(picked).filter((k) => picked[k]);
    if (!codes.length) return;
    const frequencies = {};
    (draft?.proposals || []).forEach((p) => { if (picked[p.code]) frequencies[p.code] = p.frequency; });
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/apply-duty-draft`, { codes, frequencies });
      toast.success(`Care plan updated — ${data.applied.length} task${data.applied.length > 1 ? "s" : ""} set from observed care. Review the Care plan step.`);
      setDraftOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const askCaregiver = async (s) => {
    const cname = clientName || "this client";
    const question = s.type === "entry_gap"
      ? `For ${cname}: task ${s.code} ${s.label} is on the care plan and other caregivers enter it, but it hasn't shown in your entries lately. Is it not happening on your shifts, or just not being selected?`
      : `For ${cname}: you've entered ${s.code} ${s.label}, which isn't on the care plan. Is this something you regularly do? The office will update the plan or clarify.`;
    try {
      const { data } = await api.post(`/clients/${clientId}/code-nudges`, { caregiver_name: s.caregiver, code: s.code, label: s.label, question });
      toast.success(`Question sent to ${data.sent_to} — it lands in their Things to complete`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="mt-6 rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="plan-vs-actual-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-heading text-lg font-semibold text-stone-900">Plan vs. actual — what caregivers really did</p>
          <p className="mt-0.5 text-xs text-stone-500">Observed duty entries from HHAeXchange against this care plan.{data?.data_through ? ` Duty data through ${data.data_through}.` : ""}</p>
        </div>
        <label className="flex items-center gap-2 text-xs text-stone-500">
          Period
          <select value={weeks} onChange={(e) => setWeeks(Number(e.target.value))} className="h-8 rounded-md border border-stone-300 bg-white px-2 text-xs" data-testid="pva-weeks">
            {[1, 2, 3, 4].map((w) => <option key={w} value={w}>last {w} week{w > 1 ? "s" : ""}</option>)}
          </select>
        </label>
      </div>

      {busy && !data && <p className="mt-4 text-xs text-stone-400">Loading…</p>}
      {!busy && data && data.rows.every((r) => !r.entries) && (
        <p className="mt-4 rounded-lg border border-dashed border-stone-300 p-5 text-center text-xs text-stone-400" data-testid="pva-empty">
          No caregiver duty entries for this period — upload the weekly HHAeXchange duties export from the Clients page.
        </p>
      )}
      {data && data.rows.some((r) => r.entries) && (
        <>
          <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="pva-summary">
            <span className="rounded-full border border-stone-200 bg-stone-50 px-3 py-1 text-xs text-stone-600">{data.totals.on_plan} tasks on plan</span>
            <span className="rounded-full border border-stone-200 bg-stone-50 px-3 py-1 text-xs text-stone-600">{data.totals.with_activity} with recorded activity</span>
            <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${data.totals.flagged ? "border-[#C0392B]/40 bg-[#C0392B]/10 text-[#C0392B]" : "border-[#27AE60]/40 bg-[#27AE60]/10 text-[#1e8449]"}`} data-testid="pva-flagged-count">
              {data.totals.flagged ? `${data.totals.flagged} need review` : "plan matches actual care"}
            </span>
            <button onClick={openDraft} className="ml-auto rounded-full bg-primary px-3.5 py-1.5 text-xs font-semibold text-primary-foreground transition-colors hover:bg-stone-900" data-testid="pva-draft-open">
              {draftOpen ? "Close draft" : "Draft plan updates from actual care"}
            </button>
          </div>

          {patterns?.suspects?.length > 0 && (
            <div className="mt-4 rounded-lg border border-stone-100 p-3" data-testid="pva-patterns">
              <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">Reading the patterns</p>
              <ul className="mt-2 space-y-2">
                {patterns.suspects.map((s, i) => (
                  <li key={`${s.type}-${s.code}-${s.caregiver}-${i}`} className="flex flex-wrap items-center gap-2 text-xs" data-testid={`pva-suspect-${i}`}>
                    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${SUSPECT_META[s.type].cls}`}>{SUSPECT_META[s.type].label}</span>
                    <span className="font-semibold text-stone-800">{s.code} {s.label}</span>
                    <span className="text-stone-500">{s.detail}</span>
                    {s.caregiver && (
                      <button onClick={() => askCaregiver(s)} className="rounded-full border border-primary/40 px-2.5 py-0.5 text-[11px] font-semibold text-primary hover:bg-primary hover:text-white" data-testid={`pva-ask-${i}`}>
                        Ask {s.caregiver.split(" ")[0].charAt(0).toUpperCase() + s.caregiver.split(" ")[0].slice(1).toLowerCase()}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {draftOpen && draft && (
            <div className="mt-4 rounded-lg border border-primary/25 bg-primary/[0.03] p-4" data-testid="pva-draft">
              <p className="text-xs font-semibold uppercase tracking-wide text-primary">Draft from actual care — {draft.since} to {draft.data_through}</p>
              <p className="mt-1 text-[11px] text-stone-500">Observed duties mapped to plan tasks with frequencies from real days worked. Check what belongs on the plan, then apply — it saves as a normal care-plan update you can keep editing.</p>
              <div className="mt-2 max-h-72 space-y-1 overflow-y-auto">
                {(draft.proposals || []).map((p) => (
                  <label key={p.code} className={`flex flex-wrap items-center gap-2 rounded-md px-2 py-1.5 text-xs ${p.already_on_plan ? "text-stone-400" : "bg-white text-stone-700"}`} data-testid={`pva-draft-row-${p.code}`}>
                    <input type="checkbox" disabled={p.already_on_plan} checked={p.already_on_plan || !!picked[p.code]}
                      onChange={(e) => setPicked({ ...picked, [p.code]: e.target.checked })} data-testid={`pva-draft-check-${p.code}`} />
                    <span className="font-semibold">{p.code} {p.label}</span>
                    <span className="text-stone-500">→ {p.already_on_plan ? `on plan (${p.planned_frequency || "set"})` : p.frequency}</span>
                    <span className="text-stone-400">· {p.days_done} days · from {p.sources.join(", ")}</span>
                  </label>
                ))}
              </div>
              {draft.skipped_codes?.length > 0 && (
                <p className="mt-2 text-[11px] text-stone-400">Not plan tasks (skipped): {draft.skipped_codes.map((s) => `${s.code} ${s.label}`.trim()).join(" · ")}</p>
              )}
              <button onClick={applyDraft} disabled={busy || !Object.values(picked).some(Boolean)}
                className="mt-3 rounded-full bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-stone-900 disabled:opacity-40" data-testid="pva-apply-draft">
                Apply {Object.values(picked).filter(Boolean).length} to the care plan
              </button>
            </div>
          )}

          <div className="mt-3 overflow-x-auto rounded-lg border border-stone-100">
            <table className="w-full text-xs">
              <thead><tr className="bg-stone-50 text-left text-stone-400">
                <th className="px-2 py-1.5">Task</th><th className="px-2 py-1.5">Planned</th>
                <th className="px-2 py-1.5 text-right">Days done</th><th className="px-2 py-1.5 text-right">Per week</th>
                <th className="px-2 py-1.5">Last done</th><th className="px-2 py-1.5">Flag</th>
              </tr></thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.code} className={`border-t border-stone-100 ${r.flag ? "" : "text-stone-400"}`} data-testid={`pva-row-${r.code}`}>
                    <td className="px-2 py-1.5 font-medium text-stone-800">{r.code} {r.label}</td>
                    <td className="px-2 py-1.5">{r.on_plan ? r.planned_frequency || "on plan" : "— not on plan —"}</td>
                    <td className="px-2 py-1.5 text-right">{r.days_done}</td>
                    <td className="px-2 py-1.5 text-right">{r.entries ? `${r.per_week}x` : "—"}</td>
                    <td className="px-2 py-1.5">{r.last_done || "—"}</td>
                    <td className="px-2 py-1.5">
                      {r.flag && (
                        <span className={`inline-block rounded-full border px-2 py-0.5 text-[10px] font-bold ${FLAG_META[r.flag].cls}`} title={r.suggestion} data-testid={`pva-flag-${r.code}`}>
                          {FLAG_META[r.flag].label}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {nudges.length > 0 && (
            <div className="mt-4" data-testid="pva-nudges">
              <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">Questions sent to caregivers</p>
              <ul className="mt-2 space-y-1.5">
                {nudges.map((n) => (
                  <li key={n.id} className="rounded-lg border border-stone-100 px-3 py-2 text-xs" data-testid={`pva-nudge-${n.id}`}>
                    <span className="font-semibold text-stone-800">{n.caregiver_name}</span>
                    <span className="text-stone-500"> — {n.code} {n.label}: {n.question}</span>
                    {n.status === "answered" ? (
                      <p className="mt-1 rounded bg-[#27AE60]/10 px-2 py-1 text-[#1e8449]"><strong>Answer:</strong> {n.answer}</p>
                    ) : (
                      <span className="ml-2 rounded-full border border-[#B7791F]/40 bg-[#B7791F]/10 px-2 py-0.5 text-[10px] font-bold text-[#8a5d14]">waiting</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
